import { z } from 'zod';

/**
 * POST /ray-paths — the 3-D vectorized light path, one shape for every engine
 * (spec 2026-10-30 §4). The producers are
 * `zemax-analysis-service/models/ray_paths.py` (the original) and
 * `analysis-service/models/ray_paths.py` (Optiland); each service's
 * `tests/test_ray_paths_contract_parity.py` holds its models to this file, so
 * the two cannot drift apart. The 3D viewer parses either engine's reply
 * with `RayPathsResponseSchema`.
 *
 * Not a manifest route: both engines are reached through the stateless
 * `/api/analysis/*` and `/api/zemax/*` proxies.
 */

/** Normalized pupil sampling patterns, point-for-point identical across the
 *  two engines in count and order (the worker's generators mirror Optiland's),
 *  so ray i of one trace is ray i of the other. `random` is deliberately
 *  absent. `count` means points along an axis for line/cross/uniform, points
 *  around the circumference for ring, RINGS for hexapolar. */
export const PUPIL_DISTRIBUTIONS = ['line_y', 'line_x', 'cross', 'ring', 'hexapolar', 'uniform'] as const;
export const PupilDistributionSchema = z.enum(PUPIL_DISTRIBUTIONS);
export type PupilDistribution = z.infer<typeof PupilDistributionSchema>;

/** The fields a client sends beside the system (`system`, `surfaces`,
 *  `schema_version`, `beam_splits`, `arm`). An over-budget request is
 *  REFUSED by name, never trimmed. The one client is `rayPathsBody` in
 *  `frontend/hooks/useRayPaths.ts`; the optional fields are the ones both
 *  producers default, and the viewer leaves them to the producer. */
export const RayPathsRequestBodySchema = z.object({
  number_of_rays: z.number().int().min(1).max(64),
  pupil_distribution: PupilDistributionSchema,
  /** Ceiling on ray paths per request (pupil points x fields x wavelengths).
   *  Both producers default it to 2000; the viewer never sends it, so the
   *  budget is the producer's policy, not the client's. */
  max_ray_paths: z.number().int().min(1).max(20000).optional(),
  /** 'all' composes every arm of a beam-split system; an int draws that arm
   *  alone. Both producers default to 'all' and ignore it without
   *  `beam_splits`; the viewer sends the pane's arm when the system has splits. */
  arms: z.union([z.literal('all'), z.number().int().min(0)]).optional(),
});
export type RayPathsRequestBody = z.infer<typeof RayPathsRequestBodySchema>;

/** What fills the space following a surface, as the engine traced it. `mirror`
 *  is the surface itself reflecting: the beam goes back into the medium it
 *  arrived in, so the space before the NEXT surface is that medium, not air. */
export const RAY_MEDIUM_KINDS = ['air', 'glass', 'mirror'] as const;
export const RayMediumSchema = z.object({
  kind: z.enum(RAY_MEDIUM_KINDS),
  /** The glass as the engine names it; null for air, a mirror, and a glass with no catalog name (a model glass). */
  name: z.string().nullable(),
});
export type RayMedium = z.infer<typeof RayMediumSchema>;

/** Why a ray stopped. MISS and TIR are trace errors, VIGNETTE a ray blocked
 *  at a surface's aperture; the Zemax engine also reports UNREADABLE (the batch
 *  trace returned nothing for the ray) and UNKNOWN_<raw code> for an error code
 *  not characterized, passed through rather than guessed at. */
export const RAY_FAILURE_MODES = ['MISS', 'TIR', 'VIGNETTE', 'UNREADABLE'] as const;
export const RayFailureModeSchema = z.union([z.enum(RAY_FAILURE_MODES), z.string().regex(/^UNKNOWN_-?\d+$/)]);
export type RayFailureMode = z.infer<typeof RayFailureModeSchema>;

/** One surface placed in the global frame: what a drawing needs to put it in
 *  space and to know what it is, without its shape. The one declaration of a
 *  traced surface; `contracts/generated/ray-paths.schema.json` carries it to
 *  the three Python producers' models, whose parity tests hold them to it. */
export const SurfacePlacementSchema = z.object({
  /** The engine's row index: the LDE row on Zemax, the LLM JSON row of the
   *  traced (for a beam-split arm, projected) chain on Optiland; 0 = OBJ when
   *  the object plane is included. */
  index: z.number().int(),
  /** 0 = primary, k = beam_splits[k-1]. */
  arm: z.number().int(),
  /** Vertex in the global frame, lens units. */
  x: z.number(),
  y: z.number(),
  z: z.number(),
  /** 3x3 local->global rotation, row-major; column 3 is the local +Z axis in global coordinates. */
  rotation: z.array(z.array(z.number()).length(3)).length(3),
  /** Native LDE SemiDiameter in lens units; zero for the object-plane marker and coordinate breaks. */
  semi_diameter: z.number(),
  /** The medium following the surface, stamped once by the engine that traced it. A coordinate break carries the medium it sits in. */
  medium: RayMediumSchema,
  /** A plain STANDARD surface with air arriving and air following, not the
   *  image: the object, the stop or a dummy plane. It bends no light and is no
   *  body. The medium arriving is walked across coordinate breaks and mirrors
   *  (a reflection returns the beam into the medium it came from). */
  is_reference_plane: z.boolean(),
  is_stop: z.boolean(),
  /** True for the OBJ entry (finite object only). */
  is_object_plane: z.boolean(),
  /** True for Coordinate Break rows — frame bookkeeping with no profile to draw. */
  is_coordinate_break: z.boolean(),
  /** Center thickness in lens units; null for the object plane and for infinite thickness. */
  thickness: z.number().nullable(),
});
export type SurfacePlacement = z.infer<typeof SurfacePlacementSchema>;

/** A traced surface with the local-frame sag profile a client needs to draw its shape. */
export const RayPathSurfaceSchema = SurfacePlacementSchema.extend({
  /** Sag-profile Y samples in the surface's LOCAL frame; empty for the object plane and coordinate breaks. */
  sag_y: z.array(z.number()),
  /** Sag-profile Z samples paired with sag_y, local frame. */
  sag_z: z.array(z.number()),
});
export type RayPathSurface = z.infer<typeof RayPathSurfaceSchema>;

const Vector3Schema = z.array(z.number()).length(3);

/** One traced ray as a polyline through the system, in the global frame. */
export const RayPath3DSchema = z.object({
  /** 1-based. */
  field_index: z.number().int(),
  /** 1-based. */
  wavelength_index: z.number().int(),
  arm: z.number().int(),
  pupil_x: z.number(),
  pupil_y: z.number(),
  /** [Xg, Yg, Zg] ray-surface intercepts, ordered by surface. */
  points: z.array(Vector3Schema),
  /** The surface index each point sits on, 1:1 with points. An object at
   *  infinity contributes no hit and a dead ray stops early. */
  surfaces: z.array(z.number().int()),
  /** Angle of incidence in degrees at each point, 1:1 with points, in [0, 90];
   *  null at the first point of a finite-conjugate system. */
  aoi_deg: z.array(z.number().nullable()),
  /** [Lg, Mg, Ng] leaving surface 0, for object-at-infinity systems only:
   *  extrapolate backwards along it to draw the ray entering the view. */
  incoming: Vector3Schema.nullable(),
  /** [Lg, Mg, Ng] leaving the LAST point — what draws the ray on past the last surface it reached. */
  outgoing: Vector3Schema,
  /** Why the ray stopped; null when it reached the image surface. */
  failure_mode: RayFailureModeSchema.nullable(),
  /** Surface index the trace was targeting when the ray died; null when it survived. */
  failed_at: z.number().int().nullable(),
});
export type RayPath3D = z.infer<typeof RayPath3DSchema>;

/** The envelope both engines return. On `success: false` the lists are null. */
export const RayPathsResponseSchema = z.object({
  success: z.boolean(),
  errors: z.array(z.record(z.string(), z.string())),
  warnings: z.array(z.record(z.string(), z.string())),
  surfaces: z.array(RayPathSurfaceSchema).nullable(),
  rays: z.array(RayPath3DSchema).nullable(),
  /** The pattern actually sampled, echoed back. */
  pupil_distribution: PupilDistributionSchema.nullable(),
  /** Engine-specific first-order properties; the 3D viewer reads none of it. */
  paraxial: z.record(z.string(), z.unknown()).nullable(),
  rays_total: z.number().int().nullable(),
  rays_through: z.number().int().nullable(),
  /** Index of the space following each surface at the primary wavelength,
   *  object..image. One longer than `surfaces` at infinite conjugate (the
   *  object row is omitted there), 1:1 otherwise. The two engines differ in
   *  sign: the Zemax engine reports it signed as OpticStudio does (negative in
   *  mirror space, after an odd mirror count), Optiland the physical,
   *  unsigned index. The 3D viewer reads neither. */
  refractive_indices: z.array(z.number()).nullable(),
  lens_units: z.string().nullable(),
  /** Arms composed into this response; null on a single-chain system. */
  drawn_arms: z.array(z.number().int()).nullable(),
});
export type RayPathsResponse = z.infer<typeof RayPathsResponseSchema>;
